import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ContentCard from "../components/ContentCard";
import { getContents } from "../api/contentApi";

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[ي]/g, "ی")
    .replace(/[ك]/g, "ک")
    .trim();

function CoursesPage() {
  const [coursesList, setCoursesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "all");
  const [activeLevel, setActiveLevel] = useState(searchParams.get("level") || "all");

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError("");

        const contents = await getContents("course");
        setCoursesList(contents);
      } catch (err) {
        setError(err.message || "خطا در دریافت دوره‌ها");
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  useEffect(() => {
    setSearchTerm(searchParams.get("search") || "");
    setActiveCategory(searchParams.get("category") || "all");
    setActiveLevel(searchParams.get("level") || "all");
  }, [searchParams]);

  const categories = useMemo(() => {
    return [
      ...new Set(
        coursesList
          .map((item) => item.category)
          .filter(Boolean)
      ),
    ];
  }, [coursesList]);

  const levels = useMemo(() => {
    return [
      ...new Set(
        coursesList
          .map((item) => item.level)
          .filter(Boolean)
      ),
    ];
  }, [coursesList]);

  const filteredCourses = useMemo(() => {
    const normalizedSearch = normalizeText(searchTerm);

    return coursesList.filter((item) => {
      const matchesCategory =
        activeCategory === "all" || item.category === activeCategory;

      const matchesLevel = activeLevel === "all" || item.level === activeLevel;

      const searchableText = normalizeText(
        [
          item.title,
          item.summary,
          item.category,
          item.level,
          item.duration,
          Array.isArray(item.tags) ? item.tags.join(" ") : item.tags,
        ].join(" ")
      );

      const matchesSearch =
        !normalizedSearch || searchableText.includes(normalizedSearch);

      return matchesCategory && matchesLevel && matchesSearch;
    });
  }, [coursesList, searchTerm, activeCategory, activeLevel]);

  const updateFilters = (nextSearch, nextCategory, nextLevel) => {
    const params = new URLSearchParams();

    if (nextSearch?.trim()) params.set("search", nextSearch.trim());
    if (nextCategory && nextCategory !== "all") params.set("category", nextCategory);
    if (nextLevel && nextLevel !== "all") params.set("level", nextLevel);

    setSearchParams(params);
  };

  return (
    <main className="content-page">
      <section className="page-hero glass-card">
        <span className="hero-badge">مسیر یادگیری هوشمند</span>
        <h1>دوره‌های آموزشی</h1>
        <p>
          دوره‌های کاربردی برای یادگیری مهارت‌های فناوری، برنامه‌نویسی، امنیت
          سایبری و هوش مصنوعی.
        </p>
      </section>

      <section className="content-section content-section-filter-first">
        <div className="content-filter-bar glass-card">
          <div className="content-filter-search">
            <svg
              className="content-filter-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M16.5 16.5L21 21" />
            </svg>
            <input
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                updateFilters(event.target.value, activeCategory, activeLevel);
              }}
              placeholder="جستجو در دوره‌ها..."
            />
          </div>

          <div className="content-filter-chips" aria-label="فیلتر دسته‌بندی دوره‌ها">
            <button
              type="button"
              className={activeCategory === "all" ? "active" : ""}
              onClick={() => {
                setActiveCategory("all");
                updateFilters(searchTerm, "all", activeLevel);
              }}
            >
              همه دسته‌ها
            </button>

            {categories.map((category) => (
              <button
                key={category}
                type="button"
                className={activeCategory === category ? "active" : ""}
                onClick={() => {
                  setActiveCategory(category);
                  updateFilters(searchTerm, category, activeLevel);
                }}
              >
                {category}
              </button>
            ))}
          </div>

          {levels.length > 0 && (
            <div className="content-filter-chips compact" aria-label="فیلتر سطح دوره">
              <button
                type="button"
                className={activeLevel === "all" ? "active" : ""}
                onClick={() => {
                  setActiveLevel("all");
                  updateFilters(searchTerm, activeCategory, "all");
                }}
              >
                همه سطح‌ها
              </button>

              {levels.map((level) => (
                <button
                  key={level}
                  type="button"
                  className={activeLevel === level ? "active" : ""}
                  onClick={() => {
                    setActiveLevel(level);
                    updateFilters(searchTerm, activeCategory, level);
                  }}
                >
                  {level}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading && <div className="content-state glass-card">در حال دریافت دوره‌ها...</div>}

        {!loading && error && (
          <div className="content-state content-state-error glass-card">{error}</div>
        )}

        {!loading && !error && filteredCourses.length === 0 && (
          <div className="content-state glass-card">
            موردی با این فیلتر پیدا نشد.
          </div>
        )}

        {!loading && !error && filteredCourses.length > 0 && (
          <div className="content-grid">
            {filteredCourses.map((item, index) => (
              <ContentCard key={item._id || item.id} item={item} priority={index < 3} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

export default CoursesPage;
